import React, { useEffect, useState, createContext } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useSession, useFeatures } from '@cordata/buffet';
import { Alert, Row, Col, Label } from 'reactstrap';
import Select from 'react-select';
import { sortBy, find } from 'lodash';
import { adminInstance as Admin } from '~/utils';

const AdminContext = createContext();

const AdminProvider = ({ children }) => {
  const [organizations, setOrganizations] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [org, setOrg] = useState(null);
  const [div, setDiv] = useState(null);

  const { hasAppKickstarter } = useFeatures();
  const { current } = useSession();

  const location = useLocation();
  const pathsWithDivFilter = ['users', 'clinical-trials', 'departments', 'outbound-emails', 'sendgrid-events'];
  const divFilter = pathsWithDivFilter.some(path => location.pathname.includes(path));

  const pathParts = location.pathname.split('/').filter(Boolean);
  // /admin/providers         => ['admin', 'providers']
  // /admin/providers/new     => ['admin', 'providers', 'new']
  // /admin/providers/123     => ['admin', 'providers', '123']

  const isTopLevelAdminPage =
    pathParts[0] === 'admin' &&
    pathParts.length === 2;

  const shouldDisableContextSelects = !isTopLevelAdminPage;

  useEffect(() => {
    if (hasAppKickstarter) {
      Admin
        .get('/organization?all-data=1')
        .then(r => r.data.data)
        .then(r => sortBy(r, ['attributes.orgName']))
        .then(r => setOrganizations(r));
    } else if (
      current &&
      current.id &&
      current.attributes.currentDivisionUid
    ) {
      Admin
        .get(`/division/${current.attributes.currentDivisionUid}`)
        .then(r => r.data.data)
        .then((r) => {
          setDiv(r);
          localStorage.setItem('div', r.id);
          Admin
            .get(`/organization/${r.attributes.orgUid}`)
            .then(o => o.data.data)
            .then(setOrg);
        });
    }
  }, [hasAppKickstarter, current]);

  useEffect(() => {
    if (org && org.id) {
      if (hasAppKickstarter) {
        Admin
          .get(`/division?org-uid=${org.id}&all-data=1`)
          .then(r => r.data.data)
          .then(r => sortBy(r, ['attributes.divisionName']))
          .then(r => setDivisions(r));
      } else {
        setDivisions(current.attributes.accessibleDivisions
          .filter(d => (d.permissions
            ? d.permissions.map(p => p.securityPermissionName).includes('hasAppDivisionAdmin')
            : d))
          .map(d => ({
            ...d,
            id: d.divisionUid,
            attributes: {
              ...d,
            },
          })));
      }
    }
  }, [org, current]);

  useEffect(() => {
    if (organizations.length && localStorage.getItem('org')) {
      setOrg(find(organizations, ['id', localStorage.getItem('org')]));
    }
  }, [organizations, localStorage.getItem('org')]);

  return (
    <>
      <Row form>
        {hasAppKickstarter && (
          <Col md>
            <Label for="org">Organization</Label>

            <Select
              isClearable
              isDisabled={shouldDisableContextSelects}
              id="org"
              options={organizations}
              value={org}
              onChange={(o) => {
                setDivisions([]);
                setDiv(null);
                localStorage.removeItem('div');

                if (o) {
                  setOrg(o);
                  localStorage.setItem('org', o.id);
                } else {
                  setOrg(undefined);
                  localStorage.removeItem('org');
                }
              }}
              placeholder="Select your organization"
              className="form-select mb-3"
              classNamePrefix="react-select"
              getOptionValue={o => o.id}
              getOptionLabel={o => o.attributes.orgName}
            />
          </Col>
        )}

        {org && divFilter && (
          <Col sm>
            <Label for="div">Division</Label>

            <Select
              isClearable
              isDisabled={shouldDisableContextSelects}
              id="div"
              options={divisions}
              value={div}
              onChange={(o) => {
                if (o) {
                  setDiv(o);
                  localStorage.setItem('div', o.id);
                } else {
                  setDiv(undefined);
                  localStorage.removeItem('div');
                }
              }}
              placeholder="Select your division"
              className="form-select mb-3"
              classNamePrefix="react-select"
              getOptionValue={o => o.id}
              getOptionLabel={o => o.attributes.divisionName}
            />
          </Col>
        )}
      </Row>

      {hasAppKickstarter && !localStorage.getItem('org') && (<Alert color="info">Please select an organization first.</Alert>)}

      <AdminContext.Provider
        value={{
          org,
          div,
        }}
      >
        {children}
      </AdminContext.Provider>
    </>
  );
};

AdminProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AdminProvider;

export {
  AdminContext,
};
